import { useEffect, useState } from 'react';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Has this coach ever written to this player in this thread?
 *
 * Nothing already loaded can answer it. The conversation document carries
 * `lastMessage` (text only, no sender), `lastMessageAt` and `unreadCount` — and
 * `sendMessage` bumps `unreadCount[recipient]` for EVERY message type, so a
 * player with unread items may simply have an automated "Your lesson is ready!"
 * waiting. There is no `lastMessageSenderId` field to read.
 *
 * So it takes the messages subcollection, and the filter has to be two-part:
 * `deliverLesson` sends its system message with the COACH's uid as the sender,
 * so `senderId == coachId` alone is true the moment a lesson is delivered, from
 * a message the coach never wrote. `type == 'text'` is what separates a note
 * they typed from the machinery.
 *
 * Cost: one `getDocs` with `limit(1)` per mount of the screen that asks. Two
 * equality filters, which Firestore serves from single-field indexes, so no
 * composite index. It is not a listener: the only way to message from these
 * screens navigates away or closes the panel, so the screen remounts on return
 * and re-asks. A second tab would not update it live.
 *
 * `null` while unknown, so a caller can render nothing rather than flashing a
 * nudge that a moment later proves wrong.
 */
export function useCoachHasMessaged(
  conversationId: string | undefined,
  coachId: string | undefined,
): boolean | null {
  const [hasMessaged, setHasMessaged] = useState<boolean | null>(null);

  useEffect(() => {
    if (!conversationId || !coachId) return;
    let cancelled = false;
    /* No synchronous reset here: setting state at the top of an effect is a
       cascading render, and the value is already null on mount. A change of
       conversation resets it in the resolver instead. */
    getDocs(query(
      collection(db, 'conversations', conversationId, 'messages'),
      where('senderId', '==', coachId),
      where('type', '==', 'text'),
      limit(1),
    ))
      .then((snap) => { if (!cancelled) setHasMessaged(!snap.empty); })
      /* Unknown stays unknown on failure: no nudge is better than one that
         cannot be dismissed by doing the thing it asks. */
      .catch(() => { if (!cancelled) setHasMessaged(true); });
    return () => { cancelled = true; };
  }, [conversationId, coachId]);

  return hasMessaged;
}
